import React, { useState, useEffect, useMemo } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import useAuth from "../hooks/useAuth";
import api from "./api";
import "./Doctors.css";
import "./TherapistDashboard.css";

const PIE_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6'];

/* ───── status config ───── */
const STATUS_CFG = {
  missed:   { label: 'Missed',   bg: '#fee2e2', color: '#991b1b', border: '#ef4444' },
  late:     { label: 'Late',     bg: '#fef3c7', color: '#92400e', border: '#f59e0b' },
  'on-time':{ label: 'On-Time',  bg: '#d1fae5', color: '#065f46', border: '#10b981' },
  upcoming: { label: 'Upcoming', bg: '#dbeafe', color: '#1e40af', border: '#3b82f6' },
};

/* ───── analyse every recurrence across all checklists ───── */
const analyzeRecurrences = (checklists) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const rows = [];
  checklists.forEach(cl => {
    (cl.recurrences || []).forEach(rec => {
      const dueDate = new Date(rec.date);
      dueDate.setHours(0, 0, 0, 0);
      const completion = (cl.completions || []).find(c => {
        const cd = new Date(c.date); cd.setHours(0, 0, 0, 0);
        return cd.getTime() === dueDate.getTime();
      });
      let status, daysOverdue = 0;
      if (completion) {
        status = completion.isLate ? 'late' : 'on-time';
      } else if (dueDate < today) {
        status = 'missed';
        daysOverdue = Math.floor((today - dueDate) / 86400000);
      } else {
        status = 'upcoming';
      }
      rows.push({
        checklistId: cl._id,
        title: cl.title,
        assignee: cl.assignee,
        frequency: cl.frequency,
        description: cl.description,
        dueDate,
        status,
        daysOverdue,
        label: rec.label,
        completedBy: completion?.markedBy || '',
      });
    });
  });
  return rows;
};

/* ───── default "2 months ago" as YYYY-MM-DD ───── */
const twoMonthsAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 2);
  return d.toISOString().split('T')[0];
};

/* ───── format date for display ───── */
const fmtDate = d => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/* ───── per-checklist summary for the top-level table ───── */
const buildChecklistSummaries = (checklists, allRows) => {
  return checklists.map(cl => {
    const rows = allRows.filter(r => r.checklistId === cl._id);
    const missed = rows.filter(r => r.status === 'missed').length;
    const late = rows.filter(r => r.status === 'late').length;
    const onTime = rows.filter(r => r.status === 'on-time').length;
    const upcoming = rows.filter(r => r.status === 'upcoming').length;
    const total = rows.length;
    const worstOverdue = rows.reduce((mx, r) => Math.max(mx, r.daysOverdue), 0);
    return { ...cl, missed, late, onTime, upcoming, total, worstOverdue };
  });
};

/* ========================================================================= */
export default function ChecklistsDashboard() {
  const { auth } = useAuth();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCharts, setShowCharts] = useState(false);

  /* filters */
  const [statusFilter, setStatusFilter] = useState('issues'); // 'issues' | 'all' | specific status
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [freqFilter, setFreqFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(twoMonthsAgo);
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  /* ───── fetch (single GET with date range + assignee filter) ───── */
  useEffect(() => { fetchChecklists(); }, [dateFrom, dateTo, assigneeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchChecklists = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('from', dateFrom);
      if (dateTo) params.append('to', dateTo);
      if (assigneeFilter) params.append('assignees', assigneeFilter);
      params.append('_', Date.now());
      const res = await api.get(`${process.env.REACT_APP_URL}/checklists?${params.toString()}`);
      setChecklists(res.data || []);
    } catch (err) {
      console.error('Failed to fetch checklists', err);
    }
    setLoading(false);
  };

  /* ───── derived data (date-filtered) ───── */
  const allRowsRaw = useMemo(() => analyzeRecurrences(checklists), [checklists]);

  const allRows = useMemo(() => {
    let rows = allRowsRaw;
    if (dateFrom) { const d = new Date(dateFrom); d.setHours(0,0,0,0); rows = rows.filter(r => r.dueDate >= d); }
    if (dateTo)   { const d = new Date(dateTo);   d.setHours(23,59,59,999); rows = rows.filter(r => r.dueDate <= d); }
    return rows;
  }, [allRowsRaw, dateFrom, dateTo]);

  const summaries = useMemo(() => buildChecklistSummaries(checklists, allRows), [checklists, allRows]);

  /* global counts */
  const counts = useMemo(() => {
    const c = { total: allRows.length, missed: 0, late: 0, 'on-time': 0, upcoming: 0 };
    allRows.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [allRows]);

  /* unique values for filter dropdowns */
  const assignees = useMemo(() => [...new Set(checklists.map(c => c.assignee).filter(Boolean))].sort(), [checklists]);
  const frequencies = useMemo(() => [...new Set(checklists.map(c => c.frequency).filter(Boolean))].sort(), [checklists]);

  /* per-therapist stats for charts */
  const therapistStats = useMemo(() => {
    const map = {};
    allRows.forEach(r => {
      const a = r.assignee || 'Unassigned';
      if (!map[a]) map[a] = { missed: 0, late: 0, onTime: 0, upcoming: 0, total: 0 };
      map[a][r.status === 'on-time' ? 'onTime' : r.status]++;
      map[a].total++;
    });
    return Object.entries(map)
      .map(([name, s]) => ({ name, ...s, compliance: s.total > 0 ? Math.round(((s.onTime) / (s.total - s.upcoming || 1)) * 100) : 0 }))
      .sort((a, b) => b.total - a.total);
  }, [allRows]);

  /* filter summaries list */
  const filtered = useMemo(() => {
    let list = summaries;
    if (statusFilter === 'issues') list = list.filter(s => s.missed > 0 || s.late > 0);
    else if (statusFilter === 'missed') list = list.filter(s => s.missed > 0);
    else if (statusFilter === 'late') list = list.filter(s => s.late > 0);
    else if (statusFilter === 'on-time') list = list.filter(s => s.onTime > 0 && s.missed === 0 && s.late === 0);
    else if (statusFilter === 'upcoming') list = list.filter(s => s.upcoming > 0);

    if (assigneeFilter) list = list.filter(s => s.assignee === assigneeFilter);
    if (freqFilter) list = list.filter(s => s.frequency === freqFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s => s.title?.toLowerCase().includes(q) || s.assignee?.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q));
    }
    /* sort: worst overdue first */
    list = [...list].sort((a, b) => b.worstOverdue - a.worstOverdue || b.missed - a.missed);
    return list;
  }, [summaries, statusFilter, assigneeFilter, freqFilter, search]);

  /* ───── summary cards config ───── */
  const cards = [
    { key: 'issues',  label: 'Anomalies',  count: summaries.filter(s => s.missed > 0 || s.late > 0).length, grad: 'linear-gradient(135deg,#ef4444,#f97316)', icon: '🚨' },
    { key: 'missed',  label: 'Missed',      count: counts.missed,      grad: 'linear-gradient(135deg,#dc2626,#b91c1c)', icon: '❌' },
    { key: 'late',    label: 'Late',         count: counts.late,        grad: 'linear-gradient(135deg,#f59e0b,#d97706)', icon: '⏰' },
    { key: 'on-time', label: 'On-Time',      count: counts['on-time'],  grad: 'linear-gradient(135deg,#10b981,#059669)', icon: '✅' },
    { key: 'all',     label: 'Total Recurrences', count: counts.total, grad: 'linear-gradient(135deg,#3b82f6,#2563eb)', icon: '📋' },
  ];

  /* ───── expanded detail: rows for a single checklist ───── */
  const getDetailRows = (clId) => {
    return allRows
      .filter(r => r.checklistId === clId)
      .sort((a, b) => b.dueDate - a.dueDate);
  };

  /* detail rows already date-filtered via allRows */

  /* ───── render ───── */
  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>Loading checklists…</div>;

  return (
    <div style={{ padding: '16px 20px', maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>Checklists Dashboard</h2>

      {/* ───── summary cards ───── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
        {cards.map(c => (
          <div key={c.key} onClick={() => setStatusFilter(c.key)}
            style={{
              background: statusFilter === c.key ? c.grad : '#fff',
              color: statusFilter === c.key ? '#fff' : '#1e293b',
              border: statusFilter === c.key ? 'none' : '1px solid #e2e8f0',
              borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
              boxShadow: statusFilter === c.key ? '0 4px 14px rgba(0,0,0,.15)' : '0 1px 4px rgba(0,0,0,.06)',
              transition: 'all .2s',
            }}>
            <div style={{ fontSize: 24 }}>{c.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{c.count}</div>
            <div style={{ fontSize: 12, fontWeight: 600, opacity: .85 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* ───── charts (collapsed by default) ───── */}
      <div style={{ marginBottom: 14 }}>
        <button onClick={() => setShowCharts(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8,
            border: '1.5px solid #6366f1', background: showCharts ? '#6366f1' : '#eef2ff',
            color: showCharts ? '#fff' : '#4338ca', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          {showCharts ? '▼ Hide' : '▶ Show'} Charts
        </button>
        {showCharts && therapistStats.length > 0 && (() => {
          const pieData = [
            { name: 'On-Time', value: counts['on-time'] || 0 },
            { name: 'Late', value: counts.late || 0 },
            { name: 'Missed', value: counts.missed || 0 },
            { name: 'Upcoming', value: counts.upcoming || 0 },
          ].filter(d => d.value > 0);
          return (
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Stacked bar chart — per therapist */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Status by Therapist</h4>
              <ResponsiveContainer width="100%" height={Math.max(200, therapistStats.length * 36)}>
                <BarChart data={therapistStats} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
                  <XAxis type="number" fontSize={10} />
                  <YAxis type="category" dataKey="name" fontSize={11} width={55} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="onTime" name="On-Time" stackId="a" fill="#10b981" />
                  <Bar dataKey="late" name="Late" stackId="a" fill="#f59e0b" />
                  <Bar dataKey="missed" name="Missed" stackId="a" fill="#ef4444" />
                  <Bar dataKey="upcoming" name="Upcoming" stackId="a" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Pie chart — overall status distribution */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Overall Status Distribution</h4>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} innerRadius={40} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Compliance bar chart */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, gridColumn: '1 / -1' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Compliance Rate (%)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={therapistStats} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis domain={[0, 100]} fontSize={10} unit="%" />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="compliance" name="Compliance" fill="#6366f1" radius={[4, 4, 0, 0]}>
                    {therapistStats.map((t, i) => (
                      <Cell key={i} fill={t.compliance >= 80 ? '#10b981' : t.compliance >= 50 ? '#f59e0b' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          );
        })()}
      </div>

      {/* ───── filter bar ───── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 8px', marginBottom: 14, alignItems: 'center' }}>
        <input
          type="text" placeholder="Search…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 130px', maxWidth: 180, padding: '4px 8px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4, outline: 'none', height: 26 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <label style={{ fontSize: 11, color: '#475569' }}>Assignee</label>
          <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}
            style={{ fontSize: 12, padding: '2px 4px', height: 26, borderRadius: 4, border: '1px solid #d1d5db', width: 95 }}>
            <option value="">All</option>
            {assignees.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <label style={{ fontSize: 11, color: '#475569' }}>Freq</label>
          <select value={freqFilter} onChange={e => setFreqFilter(e.target.value)}
            style={{ fontSize: 12, padding: '2px 4px', height: 26, borderRadius: 4, border: '1px solid #d1d5db', width: 90 }}>
            <option value="">All</option>
            {frequencies.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <label style={{ fontSize: 11, color: '#475569' }}>From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ fontSize: 12, padding: '2px 4px', height: 26, borderRadius: 4, border: '1px solid #d1d5db', width: 115 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <label style={{ fontSize: 11, color: '#475569' }}>To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ fontSize: 12, padding: '2px 4px', height: 26, borderRadius: 4, border: '1px solid #d1d5db', width: 115 }} />
        </div>
        <button onClick={() => { setDateFrom(''); setDateTo(''); }}
          style={{ border: '1px solid #3b82f6', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 600, height: 26, cursor: 'pointer', background: (!dateFrom && !dateTo) ? '#3b82f6' : '#eff6ff', color: (!dateFrom && !dateTo) ? '#fff' : '#2563eb' }}>
          All Data
        </button>
        <button onClick={() => { setSearch(''); setAssigneeFilter(''); setFreqFilter(''); setDateFrom(twoMonthsAgo); setDateTo(''); }}
          style={{ border: '1px solid #ccc', borderRadius: 4, padding: '3px 10px', fontSize: 11, fontWeight: 600, height: 26, cursor: 'pointer', background: '#f1f5f9', color: '#475569' }}>
          Reset
        </button>
      </div>

      {/* ───── checklists table ───── */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: 14 }}>
          {statusFilter === 'issues' ? 'No anomalies found — all checklists are on track!' : 'No checklists match the current filters.'}
        </div>
      ) : (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
          {/* header */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 80px 80px 80px 80px 90px', background: '#f8fafc', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0', gap: 4 }}>
            <span>Title</span><span>Assignee</span><span>Frequency</span>
            <span style={{ textAlign: 'center' }}>Missed</span>
            <span style={{ textAlign: 'center' }}>Late</span>
            <span style={{ textAlign: 'center' }}>On-Time</span>
            <span style={{ textAlign: 'center' }}>Upcoming</span>
            <span style={{ textAlign: 'center' }}>Worst Gap</span>
          </div>

          {filtered.map((s, i) => {
            const isExpanded = expandedId === s._id;
            const detailRows = isExpanded ? getDetailRows(s._id) : [];
            return (
              <React.Fragment key={s._id}>
                <div
                  onClick={() => setExpandedId(isExpanded ? null : s._id)}
                  style={{
                    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 80px 80px 80px 80px 90px',
                    padding: '10px 14px', fontSize: 13, cursor: 'pointer', gap: 4,
                    background: i % 2 ? '#fafbfd' : '#fff',
                    borderLeft: `4px solid ${s.missed > 0 ? '#ef4444' : s.late > 0 ? '#f59e0b' : '#10b981'}`,
                    borderBottom: '1px solid #f1f5f9',
                    transition: 'background .15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 ? '#fafbfd' : '#fff'}
                >
                  <span style={{ fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 10, color: '#9ca3af' }}>{isExpanded ? '▼' : '▶'}</span>
                    {s.title}
                  </span>
                  <span style={{ color: '#475569' }}>{s.assignee}</span>
                  <span style={{ color: '#64748b', fontSize: 12 }}>{s.frequency}</span>
                  <span style={{ textAlign: 'center', fontWeight: 700, color: s.missed > 0 ? '#dc2626' : '#9ca3af' }}>{s.missed || '—'}</span>
                  <span style={{ textAlign: 'center', fontWeight: 600, color: s.late > 0 ? '#d97706' : '#9ca3af' }}>{s.late || '—'}</span>
                  <span style={{ textAlign: 'center', color: '#065f46' }}>{s.onTime || '—'}</span>
                  <span style={{ textAlign: 'center', color: '#3b82f6' }}>{s.upcoming || '—'}</span>
                  <span style={{ textAlign: 'center', fontWeight: 700, color: s.worstOverdue > 7 ? '#dc2626' : s.worstOverdue > 0 ? '#d97706' : '#9ca3af' }}>
                    {s.worstOverdue > 0 ? `${s.worstOverdue}d` : '—'}
                  </span>
                </div>

                {/* ───── expanded detail ───── */}
                {isExpanded && (
                  <div style={{ background: '#f8fafc', padding: '12px 18px', borderBottom: '2px solid #e2e8f0' }}>
                    {s.description && <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>{s.description}</div>}

                    <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                      Recurrence Details ({detailRows.length} entries)
                    </div>

                    <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#e2e8f0' }}>
                            <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Due Date</th>
                            <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>Status</th>
                            <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>Days Overdue</th>
                            <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Completed By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailRows.map((r, ri) => {
                            const cfg = STATUS_CFG[r.status] || {};
                            return (
                              <tr key={ri} style={{ background: ri % 2 ? '#f1f5f9' : '#fff' }}>
                                <td style={{ padding: '5px 10px', color: '#1e293b' }}>{fmtDate(r.dueDate)}</td>
                                <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                                  <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: cfg.bg, color: cfg.color }}>
                                    {cfg.label}
                                  </span>
                                </td>
                                <td style={{ padding: '5px 10px', textAlign: 'center', fontWeight: 600, color: r.daysOverdue > 7 ? '#dc2626' : r.daysOverdue > 0 ? '#d97706' : '#9ca3af' }}>
                                  {r.daysOverdue > 0 ? `${r.daysOverdue}d` : '—'}
                                </td>
                                <td style={{ padding: '5px 10px', color: '#475569' }}>{r.completedBy || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* per-status mini summary */}
                    <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
                      {['missed', 'late', 'on-time', 'upcoming'].map(st => {
                        const cfg = STATUS_CFG[st];
                        const cnt = detailRows.filter(r => r.status === st).length;
                        if (!cnt) return null;
                        return (
                          <div key={st} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 8, height: 8, borderRadius: 99, background: cfg.border, display: 'inline-block' }} />
                            <span style={{ fontSize: 11, color: cfg.color, fontWeight: 600 }}>{cnt} {cfg.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
